// Service-Worker-Update: die eine Quelle für "neue Version übernehmen".
//
// Zwei Regeln, beide teuer erkauft (Livetest-Punkt 3, 16.07.2026):
//
// 1. NIE selbst neu laden. Diese Funktionen melden nur, was passiert ist — der Aufrufer
//    entscheidet. Ein Reload ohne aktivierten neuen Worker liefert zwingend wieder das
//    alte Bundle aus dem alten Precache (service-worker.js bedient jede Navigation
//    daraus). Der Nutzer sieht denselben Banner erneut und hält das Update für kaputt.
//
// 2. SKIP_WAITING NICHT an der Zustandsflanke senden. Wer auf das statechange-Event
//    nach "installed" wartet und im selben Tick postMessage aufruft, verliert die
//    Nachricht: Der Worker bleibt auf "installed" stehen und reagiert nie. Im Test
//    scheiterten so 3 von 4 Versuchen. Deshalb wird auf den wartenden Worker gepollt
//    und die Nachricht wiederholt gesendet (skipWaiting ist idempotent).
//
// Beide Fehler steckten bis v3.109.0 in UpdateBanner.js; InstallBanner.js hatte
// zusätzlich gar keine Wartelogik.

const POLL_INTERVAL_MS = 200;
const RETRY_INTERVAL_MS = 500;

export const UPDATE_RESULT = {
  ACTIVATED:        'activated',         // Neuer Worker hat übernommen — Reload ist sicher
  NO_UPDATE:        'no-update',         // Kein neuer Worker vorhanden
  STILL_INSTALLING: 'still-installing',  // Neuer Worker lädt noch — Reload wäre sinnlos
  UNSUPPORTED:      'unsupported',       // Kein Service Worker (z. B. privates Fenster)
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Wartet, bis ein fertig installierter Worker bereitsteht.
// Bewusst pollend statt über statechange — siehe Regel 2 oben.
async function waitForWaiting(registration, timeoutMs) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (registration.waiting) return true;
    if (!registration.installing) return false; // nichts mehr unterwegs
    await sleep(POLL_INTERVAL_MS);
  }
  return false;
}

// Schickt SKIP_WAITING, bis der neue Worker übernimmt.
// Wiederholt, weil eine einzelne Nachricht verloren gehen kann und der wartende
// Worker zwischendurch durch einen neueren ersetzt werden kann.
function activateWaiting(serviceWorker, registration, timeoutMs) {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (ok) => {
      if (settled) return;
      settled = true;
      clearInterval(retry);
      clearTimeout(timer);
      resolve(ok);
    };

    const send = () => {
      const waiting = registration.waiting;
      if (waiting) waiting.postMessage({ type: 'SKIP_WAITING' });
    };

    serviceWorker.addEventListener('controllerchange', () => finish(true), { once: true });

    // iOS Safari feuert controllerchange nach SKIP_WAITING nicht zuverlässig — deshalb
    // zum Schluss der tatsächliche Zustand statt des Events.
    const timer = setTimeout(() => finish(registration.waiting == null), timeoutMs);

    const retry = setInterval(send, RETRY_INTERVAL_MS);
    send();
  });
}

/**
 * Versucht, einen wartenden Service Worker zu aktivieren.
 * @returns {Promise<string>} einer der UPDATE_RESULT-Werte
 */
export async function activateUpdate({
  serviceWorker = typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
  // 60s statt der früheren 8s: Der Precache umfasst ~18 MB / 76 Chunks. Über Mobilfunk
  // ist die Installation nach 8s regelmäßig nicht fertig. Die lange Frist ist jetzt
  // unbedenklich, weil danach kein blinder Reload mehr folgt.
  installTimeoutMs = 60000,
  activateTimeoutMs = 15000,
} = {}) {
  if (!serviceWorker) return UPDATE_RESULT.UNSUPPORTED;

  const registration = await serviceWorker.getRegistration();
  if (!registration) return UPDATE_RESULT.UNSUPPORTED;

  // Update evtl. noch nicht erkannt — Browser zu einem Check zwingen
  if (!registration.waiting) {
    await registration.update();
  }

  if (!registration.waiting) {
    if (!registration.installing) return UPDATE_RESULT.NO_UPDATE;
    const ready = await waitForWaiting(registration, installTimeoutMs);
    if (!ready) {
      return registration.waiting ? UPDATE_RESULT.ACTIVATED : UPDATE_RESULT.STILL_INSTALLING;
    }
  }

  const activated = await activateWaiting(serviceWorker, registration, activateTimeoutMs);
  return activated ? UPDATE_RESULT.ACTIVATED : UPDATE_RESULT.STILL_INSTALLING;
}

/**
 * Notausstieg: Worker abmelden und alle Caches löschen. Danach muss der Aufrufer neu
 * laden — die Seite kommt dann zwingend frisch vom Netz.
 * Nur für den Fall, dass ein regulärer Update-Versuch nachweislich nichts bewirkt hat.
 */
export async function resetServiceWorker({
  serviceWorker = typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
  cacheStorage = typeof caches !== 'undefined' ? caches : null,
} = {}) {
  if (serviceWorker) {
    const registration = await serviceWorker.getRegistration();
    if (registration) await registration.unregister();
  }
  if (cacheStorage) {
    const keys = await cacheStorage.keys();
    await Promise.all(keys.map((key) => cacheStorage.delete(key)));
  }
}
